// Tablero de campañas (Marketing M1-4): resultados del reporte de Meta, meta de pedidos del mes y decisiones sobre
// las propuestas.
import type { SupabaseClient } from '@supabase/supabase-js';
import { guardarParametro } from './ajustes';
import { todas } from './pedidos';
import { META_VACIA, type Donde, type MetaPedidos, type Propuesta, type ResultadoMeta } from '../logica/tablero';
import type { Pedido } from '../logica/pedidos';

const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(error.message); };

export async function cargarResultados(sb: SupabaseClient): Promise<ResultadoMeta[]> {
  const filas = await todas<ResultadoMeta>((a, b) => sb.from('meta_resultados').select('pauta_id, anuncio_id, nombre, desde, hasta, gasto, impresiones, alcance, clics, conversaciones').order('id').range(a, b));
  return filas.map(r => ({ ...r, gasto: +r.gasto }));
}
// Guarda el reporte en una sola operación; lo que ya estaba del mismo anuncio y de los mismos días se reemplaza.
export async function importarMeta(sb: SupabaseClient, filas: ResultadoMeta[]): Promise<{ filas: number; reemplazadas: number }> {
  const { data, error } = await sb.rpc('importar_meta', { datos: { filas } });
  if (error) throw new Error(error.message);
  return data as { filas: number; reemplazadas: number };
}

export async function cargarMetaPedidos(sb: SupabaseClient): Promise<MetaPedidos> {
  const { data, error } = await sb.from('parametros').select('valor').eq('clave', 'meta_pedidos').maybeSingle();
  if (error) throw new Error(error.message);
  const v = (data?.valor || {}) as Partial<MetaPedidos>;
  return { base: +(v.base ?? META_VACIA.base), meses: v.meses || {} };
}
export const guardarMetaPedidos = (sb: SupabaseClient, m: MetaPedidos) => guardarParametro(sb, 'meta_pedidos', m);

// Pedidos del mes, solo lo necesario para contar contra la meta.
export async function pedidosDesde(sb: SupabaseClient, desde: string): Promise<Pick<Pedido, 'fecha' | 'estado' | 'pauta_id' | 'mensajes_id'>[]> {
  return todas<Pick<Pedido, 'fecha' | 'estado' | 'pauta_id' | 'mensajes_id'>>((a, b) => sb.from('pedidos').select('fecha, estado, pauta_id, mensajes_id').gte('fecha', desde).order('id').range(a, b));
}

export type EstadoPropuesta = 'aprobada' | 'hecha' | 'descartada';
export interface Decision { id: number; clave: string; pauta_id: number | null; titulo: string; porque: string | null; como: string | null; donde: Donde; estado: EstadoPropuesta; decidida: string; hecha: string | null }
const COLS = 'id, clave, pauta_id, titulo, porque, como, donde, estado, decidida, hecha';
export async function cargarDecisiones(sb: SupabaseClient): Promise<Decision[]> {
  const { data, error } = await sb.from('propuestas').select(COLS).order('decidida', { ascending: false }).range(0, 999);
  if (error) throw new Error(error.message);
  return (data || []) as Decision[];
}
// Aprobar o descartar guarda la propuesta tal como se vio, para que la tarea se entienda aunque la regla ya no aplique.
export async function decidir(sb: SupabaseClient, p: Propuesta, estado: 'aprobada' | 'descartada'): Promise<Decision> {
  const { data, error } = await sb.from('propuestas').insert({ clave: p.clave, pauta_id: p.pauta_id, titulo: p.titulo, porque: p.porque, como: p.como, donde: p.donde, estado }).select(COLS).single();
  if (error) throw new Error(/duplicate key|unique/i.test(error.message) ? 'Esa propuesta ya se decidió en otra pantalla. Recarga el tablero.' : error.message);
  return data as Decision;
}
export async function marcarHecha(sb: SupabaseClient, id: number, hecha: boolean) {
  ok(await sb.from('propuestas').update({ estado: hecha ? 'hecha' : 'aprobada', hecha: hecha ? new Date().toISOString() : null }).eq('id', id));
}
export async function deshacerDecision(sb: SupabaseClient, id: number) { ok(await sb.from('propuestas').delete().eq('id', id)); }
